/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';
import type { Router } from 'expo-router';
import { buildHotelDetailsUrl } from '../../utils/hotelDetailsUrl';
import { joinHotelDetailsPath } from '../../utils/hotelDetailsRoute';
import type { NavigateToDetailParams, RouteParams } from './types';

type RouterHref = Parameters<Router['push']>[0];

const stripTrailingSlash = (path: string) => path.replace(/\/+$/, '');

const parseQueryStringToRouteParams = (query: string): RouteParams => {
  const params: RouteParams = {};

  new URLSearchParams(query).forEach((value, key) => {
    params[key] = value;
  });

  return params;
};

const normalizeDetailsSlug = (pathname: string): string => {
  let path = stripTrailingSlash(pathname) || '/';

  path = path.replace(/^\/v2\/hotels\/search(?=\/|$)/, '') || '/';
  path = path.replace(/^\/hotel(?=\/|$)/, '') || '/';
  path = stripTrailingSlash(path) || '/';

  const prefixes = ['/hotels/details', '/details'];

  for (const prefix of prefixes) {
    if (path === prefix) {
      return '';
    }

    if (path.startsWith(`${prefix}/`)) {
      return path.slice(prefix.length + 1);
    }
  }

  if (path.startsWith('/')) {
    return path.slice(1);
  }

  return path;
};

/**
 * Maps web/staging hotel-details URLs (and bare SEO slugs) to the consumer-owned
 * native details route prefix, preserving query params.
 */
export const resolveHotelDetailsUrlForNavigation = (
  url: string,
  detailsRoutePrefix: string,
): string => {
  const parsedUrl = new URL(url, 'https://www.airasia.com/');
  const slug = normalizeDetailsSlug(parsedUrl.pathname);
  const pathname = joinHotelDetailsPath(detailsRoutePrefix, slug);

  return `${pathname}${parsedUrl.search}`;
};

/**
 * On native, details navigation must pass search criteria as expo-router params.
 * Callers may pass full URLs from `buildDetailNavigationUrl`.
 */
/**
 * Normalizes a web/staging/bare-slug details URL to the consumer route prefix,
 * then rewrites query params for native expo-router navigation when needed.
 */
export const resolveDetailsRedirectHref = (
  url: string,
  detailsRoutePrefix: string,
): RouterHref =>
  resolveDetailNavigationHref(
    resolveHotelDetailsUrlForNavigation(url, detailsRoutePrefix),
  );

export const resolveDetailNavigationHref = (url: string): RouterHref => {
  if (Platform.OS === 'web') {
    return url as RouterHref;
  }

  const queryIndex = url.indexOf('?');
  const pathname = queryIndex >= 0 ? url.slice(0, queryIndex) : url;
  const query = queryIndex >= 0 ? url.slice(queryIndex + 1) : '';

  if (!query) {
    return pathname;
  }

  return {
    pathname,
    params: parseQueryStringToRouteParams(query),
  };
};

export const buildDetailNavigationUrl = (
  {
    hotelId,
    hotelName,
    locationName,
    currentSearchParams,
  }: NavigateToDetailParams,
  detailsRoutePrefix: string,
): string =>
  buildHotelDetailsUrl({
    hotelId,
    hotelName,
    locationName,
    currentSearchParams,
    useV2Path: true,
    preserveDetailsRoutePrefix: true,
    detailsRoutePrefix:
      detailsRoutePrefix === '/'
        ? '/'
        : detailsRoutePrefix.endsWith('/')
          ? detailsRoutePrefix
          : `${detailsRoutePrefix}/`,
  });
